// Scherm Taken (ontwerp Taakbeheer, https://claude.ai/artifact/K68w2pY3LCEEkBnxq1yXqP, versie 2): alle open taken van
// de gekozen klant, per klant als boom zoals de lijst in Planner (lib/tree.ts): een fase is een rij met een pijltje, de
// taken eronder ingesprongen, tot drie lagen. Per taak de eigenaars, het label, de prioriteit, de deadline, de uren uit
// Clockify en wat nog ontbreekt om uitgewerkt te zijn (lib/complete.ts). Zoeken (ook op fase), filters, "nog uit te
// werken", sorteren binnen een fase, fases in- en uitklappen. Het rondje vooraan werkt de taak af, zoals in Mijn werk.
// Een klik opent de taak in een venster over de lijst (?taak=, screens/Taak.tsx); de lijst houdt haar filters en
// scrollpositie. Filters en ingeklapte fases onthoudt de browser, ook na herladen (lib/prefs.ts). De filter Afgewerkt
// toont de taken van de laatste 90 dagen (data.done).
// Voor planner en medewerker, met dezelfde rechten als de lade.
import { Fragment, useMemo, type KeyboardEvent, type MouseEvent } from "react";
import { useSearchParams } from "react-router";
import { fmtHours, useClockifyLive, type TaskHours } from "../lib/api-clockify";
import { useLatestUpdates, type LatestUpdate } from "../lib/api-history";
import { catOf, labelColor, labelFor } from "../lib/board";
import { count, isOpen } from "../lib/cleanup";
import { missing, MISSING_TEXT, type Missing } from "../lib/complete";
import { faseChain } from "../lib/fases";
import { useIndex, useMe, usePlanning, useTaskOps } from "../lib/hooks";
import { Icon } from "../lib/icons";
import { isBool, isStr, isStrList, usePref } from "../lib/prefs";
import { ddmm, deadline, PRIORITIES, priorityLabel, type Category } from "../lib/planning";
import { rightsOf, useSettings } from "../lib/settings";
import { faseTree, TREE_SORTS, type TreeRow, type TreeSort } from "../lib/tree";
import type { Fase, Me, PlanningData, Task } from "../lib/types";
import { Av } from "../components/Avatar";
import { clockifyReady } from "../components/TaskCard";
import { useAppState } from "../shell/AppState";
import { LoadError } from "../shell/Layout";
import { FaseModal } from "./Fase";
import { TaskModal } from "./Taak";

type Status = "open" | Category | "afgewerkt";
interface Filters { q: string; owner: string; status: Status; prio: string; todo: boolean; sort: TreeSort }
const DEFAULT: Filters = { q: "", owner: "", status: "open", prio: "", todo: false, sort: "planner" };
const STATUS: [Status, string][] = [["open", "Alle open"], ["actief", "Actief"], ["geparkeerd", "Geparkeerd"], ["afsluiten", "Klaar om af te sluiten"], ["afgewerkt", "Afgewerkt (90 dagen)"]];
// filters en ingeklapte fases onthoudt de browser (lib/prefs.ts): ook na een ander scherm of herladen
const isFilters = (v: unknown): v is Filters => {
  if (!v || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  return isStr(o.q) && isStr(o.owner) && STATUS.some(([k]) => k === o.status) && isStr(o.prio) && isBool(o.todo) && TREE_SORTS.some(([k]) => k === o.sort);
};

/** Uren uit Clockify: gelogd tegenover de schatting met een balkje; zonder schatting zegt de cel dat. */
function HoursCell({ h }: { h: TaskHours | undefined }) {
  if (h?.none) return <span className="t-mute" style={{ fontSize: 12.5 }} title="Bewust zonder Clockify-taak">–</span>;
  const logged = h?.logged || 0, est = h && !h.missing ? h.estimate ?? null : null;
  if (est == null) {
    return logged
      ? <div className="p-tk-hrs"><span>{fmtHours(logged)} gelogd</span><span className="t-mute">geen schatting</span></div>
      : <span className="t-mute" style={{ fontSize: 12.5 }} title={h ? "Nog geen schatting in Clockify" : "Nog niet gekoppeld aan een Clockify-taak"}>geen schatting</span>;
  }
  const over = logged > est;
  return (
    <div className="p-tk-hrs" title={fmtHours(logged) + " gelogd van " + fmtHours(est) + " geschat"}>
      <span className={over ? "t-err" : ""}>{fmtHours(logged).replace(" u", "")} van {fmtHours(est)}</span>
      <span className="bar"><i className={over ? "over" : ""} style={{ width: Math.min(100, (logged / est) * 100) + "%" }} /></span>
    </div>
  );
}

interface RowCtx {
  data: PlanningData; me: Me; clockify: boolean; write: boolean; planner: boolean; finishOthers: boolean;
  hoursOf: (t: Task) => TaskHours | undefined; missOf: (t: Task) => Missing[]; latest: Record<string, LatestUpdate> | undefined;
  bucketName: (id: string) => string; onOpen: (id: string) => void; onFinish: (t: Task) => void; onToggle: (f: Fase) => void; onOpenFase: (id: string) => void;
}

function TaskRow({ t, level, c }: { t: Task; level: number; c: RowCtx }) {
  const act = catOf(t) === "actief", d = deadline(t.end, c.data.today), l = labelFor(t), m = c.missOf(t);
  const done = t.checklist.filter((x) => x.done).length, up = c.latest?.[t.id];
  const own = t.owners.some((o) => o.personId === c.me.id);
  const may = c.write && !t.done && (c.planner || own || (c.finishOthers && t.owners.length > 0));
  const sub = [c.bucketName(t.bucketId), t.checklist.length ? "checklist " + done + "/" + t.checklist.length : null, up ? "update " + ddmm(up.at.slice(0, 10)) + " · " + up.who : null].filter(Boolean).join(" · ");
  const finish = (e: MouseEvent) => { e.stopPropagation(); c.onFinish(t); };
  const key = (e: KeyboardEvent<HTMLDivElement>) => { if (e.key === "Enter" && e.target === e.currentTarget) { e.preventDefault(); c.onOpen(t.id); } };
  return (
    <div className={"p-tk-row" + (t.done ? " done" : "") + (t.pending ? " pending" : "")} role="button" tabIndex={0} onClick={() => c.onOpen(t.id)} onKeyDown={key}>
      <span><button type="button" className="p-circ p-tk-circ" title={t.done ? "Afgewerkt" : may ? "Afwerken" : "Afwerken doet de eigenaar of de planner"} aria-label="Afwerken" disabled={!may} onClick={finish}><Icon name="check" size={11} /></button></span>
      <span className="p-tk-ti" style={{ paddingLeft: level * 22 + 22 }}><span className="p-tk-tt"><b>{t.title}</b><span>{sub}</span></span></span>
      <span className="p-tk-avs">
        {t.owners.length
          ? t.owners.map((o) => { const p = c.data.people.find((x) => x.id === o.personId); return <Av key={o.personId} id={o.personId} init={p?.initials || "?"} cls="p-av s" />; })
          : <span className="p-av s none" title="Zonder eigenaar"><Icon name="userx" size={12} /></span>}
      </span>
      <span>{t.done ? <span className="p-lbl"><i style={{ background: "var(--ok-dot)" }} />Afgewerkt</span> : l ? <span className="p-lbl"><i style={{ background: labelColor(l) }} />{l}</span> : <span className="t-mute" style={{ fontSize: 12.5 }}>geen label</span>}</span>
      <span style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {!t.done && t.priority <= 3 && <span className={"p-prio p" + t.priority}>{priorityLabel(t.priority)}</span>}
        {!t.done && t.critical && act && <span className="p-crit">Kritisch</span>}
      </span>
      <span>{t.done
        ? <span className="p-meta-i t-mute" style={{ fontSize: 13 }}><Icon name="check" size={13} />afgewerkt · gewijzigd {ddmm(t.modified)}</span>
        : act ? <span className={"p-dl" + (d.tone ? " t-" + d.tone : "")} style={{ fontSize: 13 }}><Icon name={d.key === "verlopen" ? "alert" : "cal"} size={13} />{d.text}</span>
        : <span className="p-meta-i t-mute" style={{ fontSize: 13 }}><Icon name="cal" size={13} />{ddmm(t.end)}</span>}</span>
      <span>{c.clockify && !t.done && <HoursCell h={c.hoursOf(t)} />}</span>
      <span className="p-tk-miss">{t.done ? null : m.length ? m.map((k) => <span key={k}>{MISSING_TEXT[k]}</span>) : <span className="ok"><Icon name="check" size={13} />uitgewerkt</span>}</span>
    </div>
  );
}

function FaseRow({ r, c }: { r: Extract<TreeRow, { kind: "fase" }>; c: RowCtx }) {
  const f = r.fase, open = r.tasks.filter((t) => !t.done);
  const est = c.clockify ? open.map((t) => c.hoursOf(t)).filter((h): h is TaskHours => !!h && !h.none && !h.missing && h.estimate != null) : [];
  const estSum = est.reduce((a, h) => a + (h.estimate || 0), 0), logSum = est.reduce((a, h) => a + (h.logged || 0), 0);
  const todo = open.filter((t) => c.missOf(t).length).length;
  // het pijltje klapt in en uit; de rest van de rij opent het fasevenster
  const key = (e: KeyboardEvent<HTMLDivElement>) => { if (e.key === "Enter" && e.target === e.currentTarget) { e.preventDefault(); c.onOpenFase(f.id); } };
  const toggle = (e: MouseEvent) => { e.stopPropagation(); c.onToggle(f); };
  return (
    <div className="p-tk-row fase" role="button" tabIndex={0} onClick={() => c.onOpenFase(f.id)} onKeyDown={key}>
      <span />
      <span className="p-tk-ti" style={{ paddingLeft: r.level * 22 }}>
        <button type="button" className={"p-tk-chev" + (r.closed ? " closed" : "")} aria-expanded={!r.closed} aria-label={r.closed ? "Fase uitklappen" : "Fase inklappen"} title={r.closed ? "Uitklappen" : "Inklappen"} onClick={toggle}><Icon name="down" size={15} /></button>
        <span className="p-tk-tt"><b>{f.title}</b><span>fase · {f.done} van {f.total} af{r.closed ? " · ingeklapt, " + count(open.length) : ""}</span></span>
      </span>
      <span />
      <span>{f.labels[0] && <span className="p-lbl"><i style={{ background: labelColor(f.labels[0].name) }} />{f.labels[0].name}</span>}</span>
      <span />
      <span><span className="p-meta-i t-mute" style={{ fontSize: 13 }}><Icon name="cal" size={13} />{ddmm(f.start)} → {ddmm(f.end)}</span></span>
      <span>{estSum > 0 && <HoursCell h={{ clockifyTaskId: "", estimate: estSum, logged: logSum }} />}</span>
      <span className="t-mute" style={{ fontSize: 12.5 }}>{todo ? count(todo) + " nog uit te werken" : ""}</span>
    </div>
  );
}

export function Taken() {
  const { data, error, refetch } = usePlanning();
  const { data: me } = useMe();
  const { data: settings } = useSettings();
  const idx = useIndex(data);
  const { klant } = useAppState();
  const ops = useTaskOps();
  const [params, setParams] = useSearchParams();
  const { data: liveData } = useClockifyLive();
  const { data: latest } = useLatestUpdates();
  const [f, setF] = usePref<Filters>("tp.taken.filters", DEFAULT, isFilters);
  const [closedIds, setClosedIds] = usePref<string[]>("tp.taken.ingeklapt", [], isStrList);
  const closed = useMemo(() => new Set(closedIds), [closedIds]);
  const set = (p: Partial<Filters>) => setF((x) => ({ ...x, ...p }));

  const live = liveData && clockifyReady(liveData.clockify) ? liveData : null;
  const clockify = !!live;
  const hoursOf = (t: Task) => live?.tasks[t.id];
  const missOf = (t: Task) => missing(t, hoursOf(t), clockify);

  // de taken die door de filters komen; zoeken vindt een taak ook op elke fase erboven
  const visible = useMemo(() => {
    if (!data) return [];
    const pool = f.status === "afgewerkt" ? data.done || [] : data.tasks.filter(isOpen);
    const q = f.q.trim().toLowerCase();
    return pool.filter((t) => {
      if (klant && t.projectId !== klant) return false;
      if (f.status !== "open" && f.status !== "afgewerkt" && catOf(t) !== f.status) return false;
      if (f.owner === "none" ? t.owners.length : f.owner && !t.owners.some((o) => o.personId === f.owner)) return false;
      if (f.prio && t.priority !== Number(f.prio)) return false;
      if (f.todo && !missOf(t).length) return false;
      if (q && !(t.title.toLowerCase().includes(q) || (idx.klanten.get(t.projectId)?.name || "").toLowerCase().includes(q) || faseChain(t.faseId, idx.fases).some((x) => x.title.toLowerCase().includes(q)))) return false;
      return true;
    });
  }, [data, f, klant, idx, live]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data && error) return <LoadError what="De planningsdata" onRetry={() => refetch()} />;
  if (!data || !me) return <div className="p-loading">Planningsdata laden…</div>;
  const rights = rightsOf(settings?.settings), planner = me.role === "planner";
  const open = data.tasks.filter((t) => isOpen(t) && (!klant || t.projectId === klant));
  const todo = open.filter((t) => missOf(t).length).length;
  const klanten = data.klanten.filter((k) => !klant || k.id === klant).sort((a, b) => a.name.localeCompare(b.name, "nl", { sensitivity: "base" }));
  const owners = data.people.slice().sort((a, b) => a.name.localeCompare(b.name));
  const ctx: RowCtx = {
    data, me, clockify, write: me.canWrite, planner, finishOthers: rights.finishOthers, hoursOf, missOf, latest,
    bucketName: (id) => idx.buckets.get(id)?.name || "",
    onOpen: (id) => setParams((p) => { p.delete("fase"); p.set("taak", id); return p; }),
    onFinish: (t) => { ops.run(t, [{ kind: "progress", progress: 1 }], "Afgewerkt: " + t.title).catch(() => {}); },
    onToggle: (fase) => setClosedIds((s) => (s.includes(fase.id) ? s.filter((id) => id !== fase.id) : [...s, fase.id])),
    onOpenFase: (id) => setParams((p) => { p.delete("taak"); p.set("fase", id); return p; }),
  };
  const sections = klanten.map((k) => {
    const tasks = visible.filter((t) => t.projectId === k.id);
    if (!tasks.length) return null;
    return { k, n: tasks.length, rows: faseTree(tasks, data.fases.filter((x) => x.projectId === k.id), idx.fases, closed, f.sort, data.today) };
  }).filter((s): s is NonNullable<typeof s> => !!s);
  const faseIds = sections.flatMap((s) => s.rows.filter((r) => r.kind === "fase").map((r) => (r as Extract<TreeRow, { kind: "fase" }>).fase.id));
  const anyClosed = closed.size > 0;
  const sel = params.get("taak");
  const selTask = sel ? data.tasks.find((t) => t.id === sel) || (data.done || []).find((t) => t.id === sel) : null;
  const closeTask = () => setParams((p) => { p.delete("taak"); return p; });
  const selFase = !sel && params.get("fase") ? data.fases.find((f) => f.id === params.get("fase")) : null;
  const closeFase = () => setParams((p) => { p.delete("fase"); return p; });

  return (
    <>
      <div className="p-head">
        <div><h1>Taken</h1><p>{count(open.length).replace("taak", "open taak").replace("taken", "open taken")} · {klanten.length === 1 ? klanten[0].name : klanten.length + " klanten"} · {todo} nog uit te werken</p></div>
        <div className="p-tools">
          <label className="ex-search p-search"><Icon name="search" size={15} /><input type="text" placeholder="Zoek op titel, fase of klant" aria-label="Zoek op titel, fase of klant" value={f.q} onChange={(e) => set({ q: e.target.value })} /></label>
          <select className="ex-input p-tk-sel" aria-label="Sorteren binnen een fase" value={f.sort} onChange={(e) => set({ sort: e.target.value as TreeSort })}>
            {TREE_SORTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
      </div>
      <div className="p-tk-filters">
        <select className="ex-input p-tk-sel" aria-label="Eigenaar" value={f.owner} onChange={(e) => set({ owner: e.target.value })}>
          <option value="">Iedereen</option><option value="none">Zonder eigenaar</option>
          {owners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select className="ex-input p-tk-sel" aria-label="Status" value={f.status} onChange={(e) => set({ status: e.target.value as Status })}>
          {STATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select className="ex-input p-tk-sel" aria-label="Prioriteit" value={f.prio} onChange={(e) => set({ prio: e.target.value })}>
          <option value="">Elke prioriteit</option>
          {PRIORITIES.map(([p, l]) => <option key={p} value={p}>{l}</option>)}
        </select>
        <button type="button" className={"p-tog" + (f.todo ? " on" : "")} aria-pressed={f.todo} onClick={() => set({ todo: !f.todo })}><Icon name="alert" size={13} />Nog uit te werken</button>
        <span style={{ flex: 1 }} />
        <button type="button" className="p-tog" onClick={() => setClosedIds(anyClosed ? [] : faseIds)}><Icon name={anyClosed ? "down" : "right"} size={13} />{anyClosed ? "Fases uitklappen" : "Fases inklappen"}</button>
      </div>
      <section className="p-card p-tk-list">
        <div className="p-tk-cols"><span /><span>Taak</span><span>Eigenaar</span><span>Label</span><span>Prioriteit</span><span>Deadline</span><span>{clockify ? "Uren" : ""}</span><span>Ontbreekt nog</span></div>
        {/* de rijen scrollen binnen de kaart, zoals in Mijn werk: het scherm zelf scrolt niet */}
        <div className="p-tk-body">
          {sections.map((s) => (
            <Fragment key={s.k.id}>
              <div className="p-sec"><span>{s.k.name}</span><span>{count(s.n)}</span></div>
              {s.rows.map((r) => (r.kind === "fase" ? <FaseRow key={"f" + r.fase.id} r={r} c={ctx} /> : <TaskRow key={r.task.id} t={r.task} level={r.level} c={ctx} />))}
            </Fragment>
          ))}
          {!sections.length && <p className="p-tk-empty">{f.status === "afgewerkt" ? "Niets afgewerkt in de laatste 90 dagen in deze selectie." : "Geen taken in deze selectie."}</p>}
        </div>
      </section>
      {selTask && <TaskModal key={selTask.id} task={selTask} data={data} onClose={closeTask} />}
      {selFase && <FaseModal key={selFase.id} fase={selFase} data={data} onClose={closeFase} />}
      {sel && !selTask && (
        <>
          <button type="button" className="p-scrim" aria-label="Sluiten" onClick={closeTask} />
          <div className="p-tk-modal p-tk-modal-s" role="dialog" aria-modal="true" aria-label="Taak niet gevonden">
            <div className="p-tk-page"><p className="p-empty">Deze taak staat niet in de planning: ze is afgewerkt (langer dan 90 dagen geleden), verwijderd, of het adres klopt niet.</p><div><button type="button" className="ex-btn" onClick={closeTask}>Sluiten</button></div></div>
          </div>
        </>
      )}
    </>
  );
}
